import { CameraAdapter, CameraConfig, CameraFrame, CameraError } from './CameraAdapter';

const DEFAULT_WIDTH = 640;
const DEFAULT_HEIGHT = 480;
const DEFAULT_QUALITY = 0.7;

export class BrowserCameraAdapter implements CameraAdapter {
  readonly adapterName = 'BrowserCamera';

  private stream: MediaStream | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private canvasElement: HTMLCanvasElement | null = null;
  private config: CameraConfig = {};

  async connect(config?: CameraConfig): Promise<void> {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new CameraError(
        'getUserMedia non supporté dans ce navigateur',
        'NOT_SUPPORTED',
      );
    }

    this.config = config || {};

    const width = this.config.width || DEFAULT_WIDTH;
    const height = this.config.height || DEFAULT_HEIGHT;

    const constraints: MediaStreamConstraints = {
      video: {
        width: { ideal: width },
        height: { ideal: height },
        ...(this.config.deviceId ? { deviceId: { exact: this.config.deviceId } } : {}),
      },
      audio: false,
    };

    try {
      this.stream = await navigator.mediaDevices.getUserMedia(constraints);
    } catch (error: any) {
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        throw new CameraError('Permission caméra refusée', 'PERMISSION_DENIED', error);
      }
      if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        throw new CameraError('Aucune caméra trouvée', 'NO_DEVICE', error);
      }
      throw new CameraError(`Erreur connexion caméra: ${error.message}`, 'NOT_SUPPORTED', error);
    }

    // Créer les éléments hors-écran pour la capture
    this.videoElement = document.createElement('video');
    this.videoElement.srcObject = this.stream;
    this.videoElement.setAttribute('playsinline', 'true');
    this.videoElement.muted = true;

    await this.videoElement.play();

    this.canvasElement = document.createElement('canvas');
    this.canvasElement.width = this.videoElement.videoWidth || width;
    this.canvasElement.height = this.videoElement.videoHeight || height;
  }

  async disconnect(): Promise<void> {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    if (this.videoElement) {
      this.videoElement.srcObject = null;
      this.videoElement = null;
    }
    this.canvasElement = null;
  }

  isConnected(): boolean {
    return this.stream !== null && this.stream.active;
  }

  async captureFrame(): Promise<CameraFrame> {
    if (!this.isConnected() || !this.videoElement || !this.canvasElement) {
      throw new CameraError('Caméra non connectée', 'NOT_CONNECTED');
    }

    const ctx = this.canvasElement.getContext('2d');
    if (!ctx) {
      throw new CameraError('Impossible de créer le contexte canvas', 'CAPTURE_ERROR');
    }

    const width = this.canvasElement.width;
    const height = this.canvasElement.height;

    ctx.drawImage(this.videoElement, 0, 0, width, height);

    const quality = this.config.quality || DEFAULT_QUALITY;
    const dataUrl = this.canvasElement.toDataURL('image/jpeg', quality);

    // Extraire le base64 pur (sans le préfixe data:image/jpeg;base64,)
    const base64 = dataUrl.split(',')[1];

    return {
      dataUrl,
      base64,
      mimeType: 'image/jpeg',
      width,
      height,
      timestamp: Date.now(),
    };
  }

  getStream(): MediaStream | null {
    return this.stream;
  }

  /**
   * Lister les caméras disponibles (utile pour sélection USB vs intégrée)
   */
  static async listDevices(): Promise<Array<{ deviceId: string; label: string }>> {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return [];
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter(d => d.kind === 'videoinput')
      .map(d => ({ deviceId: d.deviceId, label: d.label || `Caméra ${d.deviceId.substring(0, 8)}` }));
  }
}
